import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Box, Typography, Stack, Button, Chip, Alert } from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useTheme } from "@mui/material/styles";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import UserContext from "./UserContext";
import HomeSummaryPanel from "./HomeSummaryPanel";

const B = {
  navy:      "#0a0e27",
  navyDark:  "#0f1233",
  navyMid:   "#3F6AC0",
  accent:    "#637cdb",
  accentLight: "#9aa9ea",
  lightBg:   "#eef1f8",
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

const DOT_GRID_LIGHT =
  "radial-gradient(circle, rgba(10,14,39,0.09) 1.2px, transparent 1.2px)";
const DOT_GRID_DARK =
  "radial-gradient(circle, rgba(255,255,255,0.045) 1.2px, transparent 1.2px)";

const TRUST = ["HIPAA Compliant", "256-bit SSL", "SOC 2 Type II", "Zero PHI to AI"];

const SHARED_FEATURES = [
  { icon: "🧪", text: "AI-powered lab report analysis" },
  { icon: "🔒", text: "HIPAA-compliant & private" },
  { icon: "🌐", text: "16-language support" },
  { icon: "📊", text: "Combined multi-report insights" },
];

const PATIENT_CARD = {
  id: "patient",
  path: "/patient-bot",
  badge: "PATIENT",
  title: "AIden — Patient Assistant",
  tagline: "Understand your results in plain language",
  description:
    "Upload your Proove lab report to AIden to receive “Aid” from Proove’s AI assistant in clearly explaining your test results – what it means, what to consider next, what you can do in your daily life to improve your health and wellness, and what questions to ask your clinicians.",
  note: "Note: if you upload a non-Proove test result, the Decision Assistant tool will not function properly.",
  disclaimer:
    "DISCLAIMER: Outputs generated by this software are intended solely for non-medical, general wellness purposes. Like all AI, AIden may occasionally misread values or make errors, and output quality depends on the clarity and completeness of the uploaded report. This product is not intended to diagnose, treat, cure, or prevent any disease, and cannot substitute for a professional medical evaluation, consultation, or treatment. Always verify results with a licensed healthcare provider.",
  accentFrom: "#3b82f6",
  accentTo:   "#6366f1",
  features: SHARED_FEATURES,
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="16" r="8" stroke="currentColor" strokeWidth="2.5" />
      <path d="M8 40c0-8.837 7.163-16 16-16s16 7.163 16 16"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

const CLINICIAN_CARD = {
  id: "clinician",
  path: "/clinician-bot",
  badge: "CLINICIAN",
  title: "ClAIre — Decision Support Assistant",
  tagline: "Evidence-based decision support for licensed providers",
  description:
    "Evidence-based data analysis for licensed clinicians from ClAIre provides a “clear” picture turning a data point into a contextual analysis – risk stratification, recommendations to consider, protocols, and peer-reviewed citations.",
  note: "Note: if you upload a non-Proove test result, the Decision Assistant tool will not function properly.",
  disclaimer:
    "DISCLAIMER: This Clinical Decision Support tool is intended solely to provide recommendations for healthcare professionals. Like all AI, ClAIre may occasionally misread values or make errors, particularly if the uploaded report is unclear, incomplete, or in a non-standard format. It is not a substitute for independent clinical judgment, diagnosis, or treatment. Healthcare professionals must independently review and verify all information and recommendations generated by this software.",
  accentFrom: "#10b981",
  accentTo:   "#0891b2",
  features: SHARED_FEATURES,
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <rect x="12" y="6" width="24" height="36" rx="3" stroke="currentColor" strokeWidth="2.5" />
      <path d="M18 16h12M18 22h12M18 28h8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="34" cy="36" r="6" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="2" />
      <path d="M32 36h4M34 34v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
};

const REPORTS_CARD = {
  id: "reports",
  path: "/my-reports",
  badge: "REPORT DASHBOARD",
  title: "Proove Report Dashboard",
  tagline: "Every report you have, in one place",
  description: "Real lab reports, converted old Proove reports, and your monthly digest updates — all organized chronologically in your private, encrypted folder. Only you can see your reports.",
  note: null,
  disclaimer: null,
  accentFrom: "#f59e0b",
  accentTo: "#ef4444",
  features: [
    { icon: "📂", text: "Folder-based chronological archive" },
    { icon: "👁️", text: "Inline PDF preview" },
    { icon: "⬇️", text: "One-click secure download" },
    { icon: "🔒", text: "Private — only your reports" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <rect x="6" y="10" width="36" height="30" rx="3" stroke="currentColor" strokeWidth="2.5" />
      <path d="M6 18h36" stroke="currentColor" strokeWidth="2.5" />
      <path d="M6 10l8-6h28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M18 28h12M24 24v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
};

const SUMMARY_VIEW_CARD = {
  id: "summary-view",
  path: "/clinician-summary-view",
  badge: "CLINICIAN TOOLS",
  title: "Summary View",
  tagline: "Every report type, one visual snapshot, per patient",
  description: "Pick a patient and see every report type they have — Opioid Risk, Pain Perception, Drug Metabolism — rendered as one visual snapshot, or download it as a branded PDF.",
  note: null,
  disclaimer: null,
  accentFrom: "#637cdb",
  accentTo:   "#3F6AC0",
  features: [
    { icon: "🔍", text: "Search any linked patient" },
    { icon: "📊", text: "Every report type, one view" },
    { icon: "📄", text: "Download as a branded PDF" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <circle cx="18" cy="18" r="10" stroke="currentColor" strokeWidth="2.5" />
      <path d="M25.5 25.5L38 38" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M13 18h10M18 13v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
};

const FREE_CONVERSION_CARD = {
  id: "free-conversion",
  path: "/free-conversion",
  badge: "FREE UPGRADE",
  title: "Convert Your Old Proove Report",
  tagline: "Bring your 2009–2017 results into today's format, free",
  description: "Upload your old Proove Genomics report and AIden will convert it into today's format — same structure, same AI-powered clinical summary as every current Proove report. Free, once per report type, forever.",
  note: null,
  disclaimer: null,
  accentFrom: "#0891b2",
  accentTo: "#3b82f6",
  features: [
    { icon: "🆓", text: "Free, one-time per report type" },
    { icon: "🧬", text: "Opioid Risk, Pain Perception & PGX" },
    { icon: "📄", text: "Same format as today's reports" },
    { icon: "🔒", text: "Only your own Proove reports" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <path d="M8 24a16 16 0 0 1 27-11.5M40 24a16 16 0 0 1-27 11.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M35 8v6h-6M13 40v-6h6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const MONTHLY_DIGEST_CARD = {
  id: "monthly-digest",
  path: "/monthly-digest",
  badge: "MONTHLY UPDATES",
  title: "Monthly Digest",
  tagline: "Genuinely new findings, verified and sourced",
  description: "See what's new each month for any of your reports — no linking required. Pick a patient, a report type, and a month to see verified, sourced updates alongside your live stats.",
  note: null,
  disclaimer: null,
  accentFrom: "#8b5cf6",
  accentTo: "#637cdb",
  features: [
    { icon: "🔎", text: "Searchable patient + month picker" },
    { icon: "📚", text: "Real, verified sources only" },
    { icon: "💳", text: "Requires an active $0.99/mo subscription" },
  ],
  icon: (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <rect x="8" y="10" width="32" height="30" rx="3" stroke="currentColor" strokeWidth="2.5" />
      <path d="M8 18h32M16 6v8M32 6v8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ),
};

const STATS = [
  { value: "16+",   label: "Languages" },
  { value: "99%",   label: "Accuracy" },
  { value: "HIPAA", label: "Compliant" },
  { value: "24/7",  label: "Available" },
];

function StatPill({ value, label, isDark }) {
  return (
    <Box sx={{
      display: "flex", flexDirection: "column", alignItems: "center",
      px: { xs: 2, sm: 3 }, py: 1.5,
      borderRadius: "12px",
      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(10,14,39,0.04)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(10,14,39,0.08)"}`,
      minWidth: 72,
    }}>
      <Typography sx={{
        fontSize: { xs: 18, sm: 22 }, fontWeight: 800, lineHeight: 1,
        background: `linear-gradient(135deg, ${B.accent} 20%, ${B.accentLight} 80%)`,
        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
      }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
        color: isDark ? "#64748b" : "#94a3b8", mt: 0.4, textTransform: "uppercase" }}>
        {label}
      </Typography>
    </Box>
  );
}

function highlightAI(text) {
  const idx = text.indexOf("AI");
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="span" sx={{ color: B.accent, fontWeight: 900 }}>AI</Box>
      {text.slice(idx + 2)}
    </>
  );
}

const AVATAR = {
  patient:   "/aiden-avatar.png",
  clinician: "/claire-avatar.png",
};

function RoleCard({ card, isDark, onClick }) {
  const [hovered, setHovered] = useState(false);

  const bg      = isDark ? "rgba(17,24,39,0.9)" : "rgba(255,255,255,0.92)";
  const textSub = isDark ? "#94a3b8" : "#475569";
  const avatarSrc = AVATAR[card.id] || null;

  return (
    <motion.div
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onClick={onClick}
      whileHover={{ y: -4, scale: 1.005 }}
      whileTap={{ scale: 0.99 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
      style={{ width: "100%", cursor: "pointer" }}
    >
      <Box sx={{
        display: "flex", flexDirection: { xs: "column", md: "row" },
        borderRadius: "20px",
        overflow: "hidden",
        position: "relative",
        background: isDark
          ? "linear-gradient(rgba(17,24,39,0.9), rgba(17,24,39,0.9)) padding-box, linear-gradient(135deg, rgba(10,14,39,0.6), rgba(99,124,219,0.4)) border-box"
          : "linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)) padding-box, linear-gradient(135deg, rgba(10,14,39,0.25), rgba(99,124,219,0.35)) border-box",
        border: "1.5px solid transparent",
        backdropFilter: "blur(16px) saturate(140%)",
        WebkitBackdropFilter: "blur(16px) saturate(140%)",
        boxShadow: hovered
          ? isDark
            ? `0 28px 72px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,124,219,0.25), 0 0 50px -18px rgba(99,124,219,0.5), inset 0 1px 0 rgba(255,255,255,0.06)`
            : `0 28px 72px rgba(10,14,39,0.18), 0 0 0 1px rgba(99,124,219,0.3), 0 0 40px -18px rgba(99,124,219,0.35), inset 0 1px 0 rgba(255,255,255,0.8)`
          : isDark
            ? "0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)"
            : "0 4px 24px rgba(10,14,39,0.08), inset 0 1px 0 rgba(255,255,255,1)",
        transition: "box-shadow 0.3s ease, transform 0.3s ease",
      }}>

        {/* Gradient accent bar — left edge on desktop (matches the row layout), top edge on mobile */}
        <Box sx={{
          width: { xs: "100%", md: 3 }, height: { xs: 3, md: "auto" },
          background: `linear-gradient(${{ xs: "to right", md: "to bottom" }[isDark ? "xs" : "xs"]}, ${card.accentFrom}, ${card.accentTo}, ${B.accent})`,
          opacity: hovered ? 1 : 0.6,
          transition: "opacity 0.3s",
          flexShrink: 0,
        }} />

        {/* Left panel — badge, avatar/icon, title, tagline */}
        <Box sx={{
          px: 3.5, py: 3, position: "relative", overflow: "hidden", flexShrink: 0,
          width: { xs: "100%", md: 280 },
          display: "flex", flexDirection: "column", justifyContent: "center",
          background: `linear-gradient(135deg, ${B.navy} 0%, ${B.navyMid} 100%)`,
        }}>
          <Box sx={{ position: "absolute", top: -60, right: -60, width: 180, height: 180,
            borderRadius: "50%", border: "1px solid rgba(99,124,219,0.1)" }} />
          <Box sx={{ position: "absolute", top: -36, right: -36, width: 110, height: 110,
            borderRadius: "50%", border: "1px solid rgba(99,124,219,0.16)" }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {avatarSrc ? (
              <motion.div
                animate={{ scale: hovered ? 1.04 : 1 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                style={{ flexShrink: 0 }}
              >
                <Box sx={{
                  width: 64, height: 64, borderRadius: "50%", overflow: "hidden",
                  border: `2.5px solid rgba(99,124,219,0.55)`,
                  boxShadow: hovered
                    ? "0 0 0 4px rgba(99,124,219,0.18), 0 8px 32px rgba(0,0,0,0.45)"
                    : "0 0 0 2px rgba(99,124,219,0.12), 0 4px 20px rgba(0,0,0,0.35)",
                  transition: "box-shadow 0.3s ease",
                }}>
                  <img
                    src={avatarSrc}
                    alt={card.title}
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 12%" }}
                  />
                </Box>
              </motion.div>
            ) : (
              <motion.div animate={{ rotate: hovered ? 10 : 0 }} transition={{ duration: 0.3 }}
                style={{ flexShrink: 0 }}>
                <Box sx={{ color: B.accent }}>{card.icon}</Box>
              </motion.div>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                bgcolor: "rgba(99,124,219,0.15)",
                border: "1px solid rgba(99,124,219,0.25)",
                borderRadius: "99px", px: 1.5, py: 0.4, mb: 0.75,
              }}>
                <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: B.accent,
                  "@keyframes pulse2": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                  animation: "pulse2 2s infinite" }} />
                <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: B.accent, letterSpacing: "0.15em" }}>
                  {card.badge}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1.25 }}>
                {highlightAI(card.title)}
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.72)", lineHeight: 1.5, mt: 1.5 }}>
            {card.tagline}
          </Typography>
        </Box>

        {/* Right panel — description, features (wrapped row), CTA */}
        <Box sx={{
          px: 3.5, py: 3, flex: 1, minWidth: 0, bgcolor: bg,
          display: "flex", flexDirection: "column", justifyContent: "center", gap: 2,
        }}>
          <Typography sx={{ fontSize: 13.5, color: textSub, lineHeight: 1.75 }}>
            {highlightAI(card.description)}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {card.features.map((f, i) => (
              <motion.div
                key={i}
                animate={{ y: hovered ? -2 : 0, opacity: hovered ? 1 : 0.9 }}
                transition={{ delay: hovered ? i * 0.04 : 0, duration: 0.2 }}
              >
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(10,14,39,0.03)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.06)"}`,
                  borderRadius: "10px", px: 1.25, py: 0.75,
                }}>
                  <Box sx={{ fontSize: 14, lineHeight: 1, flexShrink: 0 }}>{f.icon}</Box>
                  <Typography sx={{ fontSize: 12.5, color: isDark ? "#cbd5e1" : "#334155", fontWeight: 500, whiteSpace: "nowrap" }}>
                    {f.text}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>

          <Box>
            <Button
              variant="contained"
              endIcon={
                <motion.div animate={{ x: hovered ? 4 : 0 }} transition={{ duration: 0.2 }}>
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </motion.div>
              }
              sx={{
                bgcolor: B.navy, color: "#fff",
                fontWeight: 700, fontSize: 13.5, py: 1.2, px: 3,
                borderRadius: "10px", letterSpacing: "0.02em", textTransform: "none",
                boxShadow: hovered ? "0 8px 24px rgba(10,14,39,0.4)" : "0 2px 8px rgba(10,14,39,0.2)",
                transition: "all 0.25s ease",
                background: hovered ? `linear-gradient(135deg, ${B.navyDark}, ${B.navyMid})` : B.navy,
              }}
            >
              {card.id === "reports"
                ? "View Report Dashboard"
                : card.id === "history"
                  ? "Manage Update Sources"
                  : card.id === "free-conversion"
                    ? "Convert My Old Report"
                    : `Open ${card.badge.charAt(0) + card.badge.slice(1).toLowerCase()} Assistant`}
            </Button>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { user } = useContext(UserContext);

  const firstName       = user?.name?.split(" ")[0] || "there";
  const bg              = isDark ? "#080d1a" : "#f4f7ff";
  const isClinician     = user?.role === "clinician";
  const showPaymentsBadge = !isClinician;
  const reportsEnabled = process.env.REACT_APP_LAB_REPORTS_ENABLED === "true";

  // Handles landing here from a Stripe checkout redirect for an
  // already-logged-in patient (added 2026-09-03) — create_telehealth_order/
  // create_other_lab_order send already-authenticated users straight to
  // /home instead of the registration wizard, but until now nothing here
  // ever read the resulting ?telehealth=success/cancelled query params, so
  // they landed with zero acknowledgment of what just happened. Tailored
  // per kind, same reasoning as PatientRegisterWizard.js's banner — just
  // no "log in" language needed here, since they already are.
  const [checkoutBanner, setCheckoutBanner] = useState(null); // {severity, text} | null
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const kind = params.get("telehealth") ? "telehealth" : params.get("otherlab") ? "otherlab" : null;
    const status = kind ? params.get(kind) : null;
    if (!kind || (status !== "success" && status !== "cancelled")) return;
    const KIND_LABEL = { telehealth: "KidSafe Telehealth Kit order", otherlab: "lab report upload" };
    setCheckoutBanner(
      status === "success"
        ? { severity: "success", text: `Your ${KIND_LABEL[kind]} is confirmed.` }
        : { severity: "warning", text: `Your payment didn't go through, so your ${KIND_LABEL[kind]} wasn't completed. You can try again anytime from My Payments.` }
    );
    // Strip the query params so a refresh doesn't re-show the banner.
    navigate("/home", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CARDS = [
    isClinician ? CLINICIAN_CARD : PATIENT_CARD,
    ...(isClinician ? [SUMMARY_VIEW_CARD] : []),
    ...(reportsEnabled ? [REPORTS_CARD] : []),
    ...(!isClinician ? [FREE_CONVERSION_CARD, MONTHLY_DIGEST_CARD] : []),
  ];

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: bg,
      display: "flex", flexDirection: "column",
      overflow: "hidden", position: "relative",
      fontFamily: "'Metropolis', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>

      {/* Dot-grid */}
      <Box sx={{
        position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
        backgroundImage: isDark ? DOT_GRID_DARK : DOT_GRID_LIGHT,
        backgroundSize: "28px 28px", opacity: 0.9,
      }} />

      {/* Ambient blobs */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-8%",  left: "-6%",  size: 560, r: 0,   color: isDark ? "rgba(10,14,39,0.22)" : "rgba(10,14,39,0.07)" },
          { top: "55%",  right: "-6%", size: 420, r: 2.5, color: isDark ? "rgba(99,124,219,0.09)" : "rgba(99,124,219,0.13)" },
          { top: "20%",  left: "55%",  size: 340, r: 1.2, color: isDark ? "rgba(15,18,51,0.2)"   : "rgba(59,130,246,0.05)" },
        ].map((b, i) => (
          <motion.div key={i} style={{
            position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size,
            borderRadius: "50%", background: b.color, filter: "blur(100px)",
          }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 10 + i * 3, repeat: Infinity, delay: b.r, ease: "easeInOut" }}
          />
        ))}
      </Box>

      {/* Header */}
      <motion.div initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        style={{ position: "relative", zIndex: 20, flexShrink: 0 }}>
        <Box sx={{
          bgcolor: isDark ? "rgba(2,6,15,0.85)" : "rgba(10,14,39,0.97)",
          backdropFilter: "blur(16px)", borderBottom: `2px solid rgba(99,124,219,0.3)`,
        }}>
          <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, height: 64,
            display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box component={Link} to="/home"
              sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <img src="/logo-light.png" alt="Proove Genomics" style={{ height: 20, width: "auto" }} />
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ThemeToggle />
              <NavUserMenu />
            </Stack>
          </Box>
        </Box>
      </motion.div>

      {checkoutBanner && (
        <Box sx={{ position: "relative", zIndex: 10, maxWidth: 1000, mx: "auto", width: "100%", px: { xs: 2, sm: 3 }, pt: 3 }}>
          <Alert severity={checkoutBanner.severity} onClose={() => setCheckoutBanner(null)} sx={{ borderRadius: 2 }}>
            {checkoutBanner.text}
          </Alert>
        </Box>
      )}

      {!isClinician && (
        <Box sx={{ position: "relative", zIndex: 10, maxWidth: 1000, mx: "auto", width: "100%", px: { xs: 2, sm: 3 }, pt: 3 }}>
          <HomeSummaryPanel isDark={isDark} />
        </Box>
      )}

      {/* Hero */}
      <Box sx={{ position: "relative", zIndex: 10, textAlign: "center",
        pt: { xs: 7, md: 10 }, pb: { xs: 5, md: 7 }, px: 3 }}>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}>
          <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 600, letterSpacing: "0.06em",
            color: isDark ? "#64748b" : "#94a3b8", mb: 1.5, textTransform: "uppercase" }}>
            {getGreeting()}, {firstName}
          </Typography>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.18 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", justifyContent: "center", mb: 3.5 }}>
            {/* Live status pill */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              bgcolor: isDark ? "rgba(99,124,219,0.1)" : "rgba(10,14,39,0.07)",
              border: `1px solid ${isDark ? "rgba(99,124,219,0.2)" : "rgba(10,14,39,0.12)"}`,
              borderRadius: "99px", px: 2.5, py: 0.75,
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: B.accent,
                "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.25 } },
                animation: "pulse 2s infinite" }} />
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: B.accent, letterSpacing: "0.14em" }}>
                PROOVE AI — ACTIVE
              </Typography>
            </Box>

            {/* Billing status link — patients only */}
            {showPaymentsBadge && (
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.35, type: "spring", stiffness: 260, damping: 18 }}>
                <Box
                  component={Link} to="/my-payments"
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: 0.75, textDecoration: "none",
                    bgcolor: isDark ? "rgba(99,124,219,0.12)" : "rgba(99,124,219,0.08)",
                    border: "1px solid rgba(99,124,219,0.3)",
                    borderRadius: "99px", px: 2, py: 0.75,
                    "&:hover": { bgcolor: isDark ? "rgba(99,124,219,0.2)" : "rgba(99,124,219,0.14)" },
                  }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: B.accent, letterSpacing: "0.1em" }}>
                    VIEW MY PAYMENTS & CO-PAY STATUS
                  </Typography>
                </Box>
              </motion.div>
            )}
          </Box>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22 }}>
          <Typography sx={{
            fontSize: { xs: 30, sm: 40, md: 48 }, fontWeight: 800,
            color: isDark ? "#f1f5f9" : B.navy,
            lineHeight: 1.12, mb: 2.5, maxWidth: 680, mx: "auto",
            letterSpacing: "-0.02em",
          }}>
            How would you like to use the Proove{" "}
            <Box component="span" sx={{
              background: `linear-gradient(135deg, ${B.accent} 10%, ${B.accentLight} 60%, ${B.accent} 100%)`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              AI Assistant?
            </Box>
          </Typography>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}>
          <Typography sx={{
            fontSize: { xs: 15, sm: 16 }, color: isDark ? "#94a3b8" : "#3d4f6b",
            maxWidth: 460, mx: "auto", lineHeight: 1.85, mb: 4.5,
          }}>
            Choose a tool below to get started. Your session is end-to-end encrypted and
            fully compliant with HIPAA data standards.
          </Typography>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.36 }}>
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 5, gap: "8px !important" }}>
            {TRUST.map((t) => (
              <Chip key={t}
                icon={<VerifiedIcon sx={{ fontSize: "13px !important", color: `${B.accent} !important` }} />}
                label={t} size="small"
                sx={{
                  fontSize: 11.5, fontWeight: 600, height: 28,
                  bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(10,14,39,0.05)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
                  color: isDark ? "#94a3b8" : "#475569",
                  "& .MuiChip-icon": { ml: 1 },
                }}
              />
            ))}
          </Stack>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.42 }}>
          <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" sx={{ gap: "12px !important" }}>
            {STATS.map((s) => (
              <StatPill key={s.label} value={s.value} label={s.label} isDark={isDark} />
            ))}
          </Stack>
        </motion.div>
      </Box>

      {/* Cards — each RoleCard is itself a full-width horizontal banner
          (navy left panel + content right, see RoleCard below), so these
          always STACK vertically, one per row, regardless of screen size.
          Cramming banner-shaped cards side-by-side (the old
          flexDirection: {xs:"column", md:"row"} did exactly that) squeezes
          every banner into a fraction of its own width — that was the bug. */}
      <Box sx={{
        flex: 1, position: "relative", zIndex: 10,
        maxWidth: 980, mx: "auto", width: "100%",
        px: { xs: 2, sm: 3 }, pb: 8,
        display: "flex", flexDirection: "column", gap: { xs: 3, md: 4 },
      }}>
        {CARDS.map((card, i) => (
          <motion.div key={card.id}
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.38 + i * 0.14, ease: [0.34, 1.56, 0.64, 1] }}
            style={{ width: "100%", display: "flex" }}
          >
            <RoleCard card={card} isDark={isDark} onClick={() => navigate(card.path)} />
          </motion.div>
        ))}
      </Box>

      {/* Shared disclaimer — one compact line for whichever assistant role is
          shown above, instead of duplicating a long paragraph inside that
          card (which made it visibly taller/heavier than its siblings). */}
      <Box sx={{ maxWidth: 980, mx: "auto", width: "100%", px: { xs: 2, sm: 3 }, pb: 5, position: "relative", zIndex: 10 }}>
        <Typography sx={{
          fontSize: 11, color: isDark ? "#64748b" : "#94a3b8", lineHeight: 1.6, textAlign: "center",
        }}>
          {(isClinician ? CLINICIAN_CARD : PATIENT_CARD).disclaimer}
        </Typography>
      </Box>

      {/* Footer */}
      <Box sx={{
        bgcolor: isDark ? "rgba(2,6,15,0.9)" : B.navyDark,
        backdropFilter: "blur(10px)", py: 3, px: 3,
        position: "relative", zIndex: 10,
        borderTop: "1px solid rgba(255,255,255,0.07)", flexShrink: 0,
      }}>
        <Box sx={{ maxWidth: 1200, mx: "auto", display: "flex",
          alignItems: "center", justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.4)", letterSpacing: "0.04em" }}>
              © {new Date().getFullYear()} PROOVE GENOMICS. ALL RIGHTS RESERVED.
            </Typography>
            <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: "0.03em", mt: 0.4 }}>
              Powered by{" "}
              <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
                sx={{ color: "rgba(255,255,255,0.22)", textDecoration: "none", "&:hover": { color: "rgba(255,255,255,0.4)" } }}>
                Labarium Diagnostic Systems
              </Box>
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>
              AI analysis does not constitute medical advice.
            </Typography>
            <Box component={Link} to="/terms"
              sx={{ fontSize: 12, color: "rgba(99,124,219,0.7)", textDecoration: "none",
                "&:hover": { color: B.accent } }}>
              Terms of Use
            </Box>
            <Box component={Link} to="/privacy"
              sx={{ fontSize: 12, color: "rgba(99,124,219,0.7)", textDecoration: "none",
                "&:hover": { color: B.accent } }}>
              Privacy Policy
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
